import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"

/**
 * Kanban board drag-and-drop controller for support tickets.
 *
 * data-controller="support--kanban"
 *
 * Handles:
 *   - HTML5 native drag/drop to move tickets between status columns
 *   - PATCH to the transition endpoint on drop
 *   - Column count updates after successful moves
 */
export default class extends Controller {
  static targets = ["column", "card"]

  connect() {
    // Store bound handlers so they can be removed in disconnect()
    this._boundDragStart = this.#onDragStart.bind(this)
    this._boundDragEnd = this.#onDragEnd.bind(this)
    this._boundDragOver = this.#onDragOver.bind(this)
    this._boundDragEnter = this.#onDragEnter.bind(this)
    this._boundDragLeave = this.#onDragLeave.bind(this)
    this._boundDrop = this.#onDrop.bind(this)

    // Bind drag events to all cards
    this.cardTargets.forEach((card) => {
      card.setAttribute("draggable", "true")
      card.addEventListener("dragstart", this._boundDragStart)
      card.addEventListener("dragend", this._boundDragEnd)
    })

    // Bind drop zone events to all columns
    this.columnTargets.forEach((col) => {
      col.addEventListener("dragover", this._boundDragOver)
      col.addEventListener("dragenter", this._boundDragEnter)
      col.addEventListener("dragleave", this._boundDragLeave)
      col.addEventListener("drop", this._boundDrop)
    })
  }

  disconnect() {
    this.cardTargets.forEach((card) => {
      card.removeEventListener("dragstart", this._boundDragStart)
      card.removeEventListener("dragend", this._boundDragEnd)
    })

    this.columnTargets.forEach((col) => {
      col.removeEventListener("dragover", this._boundDragOver)
      col.removeEventListener("dragenter", this._boundDragEnter)
      col.removeEventListener("dragleave", this._boundDragLeave)
      col.removeEventListener("drop", this._boundDrop)
    })
  }

  // ── Drag events on cards ─────────────────────────────────

  #onDragStart(event) {
    // Don't start a card drag when the gesture begins on an interactive element
    // (the subject link, buttons). Otherwise a tiny movement while clicking the
    // title starts an HTML5 drag and the click never fires, so the ticket
    // sometimes failed to open.
    if (event.target.closest("a, button")) {
      event.preventDefault()
      return
    }

    const card = event.currentTarget
    const ticketId = card.dataset.ticketId
    event.dataTransfer.setData("text/plain", ticketId)
    event.dataTransfer.effectAllowed = "move"

    // Store source status for comparison
    card.dataset.sourceStatus =
      card.closest("[data-status]")?.dataset.status || ""

    // Add dragging visual
    requestAnimationFrame(() => {
      card.classList.add("opacity-50", "scale-95")
    })
  }

  #onDragEnd(event) {
    const card = event.currentTarget
    card.classList.remove("opacity-50", "scale-95")

    // Clean up all column highlights
    this.columnTargets.forEach((col) => {
      col.classList.remove("bg-primary/5", "ring-2", "ring-primary/20")
    })
  }

  // ── Drop zone events on columns ──────────────────────────

  #onDragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
  }

  #onDragEnter(event) {
    event.preventDefault()
    const column = event.currentTarget
    column.classList.add("bg-primary/5", "ring-2", "ring-primary/20")
  }

  #onDragLeave(event) {
    const column = event.currentTarget
    // Only remove highlight if we're actually leaving the column (not entering a child)
    if (!column.contains(event.relatedTarget)) {
      column.classList.remove("bg-primary/5", "ring-2", "ring-primary/20")
    }
  }

  async #onDrop(event) {
    event.preventDefault()
    const column = event.currentTarget
    column.classList.remove("bg-primary/5", "ring-2", "ring-primary/20")

    const ticketId = event.dataTransfer.getData("text/plain")
    const toStatus = column.dataset.status
    if (!ticketId || !toStatus) return

    // Find the dragged card
    const card = this.element.querySelector(`[data-ticket-id="${ticketId}"]`)
    if (!card) return

    const sourceStatus = card.dataset.sourceStatus
    if (sourceStatus === toStatus) return // Same column, no-op

    // Optimistically move the card to the new column
    const cardList = column.querySelector("[data-kanban-cards]")
    if (cardList) {
      cardList.appendChild(card)
    }

    // Update counts optimistically
    this.#updateColumnCounts()

    // Fire PATCH request to the transition endpoint
    const transitionUrl = card.dataset.transitionUrl
    if (!transitionUrl) return

    try {
      const response = await patch(transitionUrl, {
        body: JSON.stringify({ to_state: toStatus }),
        contentType: "application/json",
        responseKind: "json",
      })

      if (!response.ok) {
        // Revert: move card back to original column
        const origColumn = this.element.querySelector(
          `[data-status="${sourceStatus}"]`,
        )
        const origCardList = origColumn?.querySelector("[data-kanban-cards]")
        if (origCardList) {
          origCardList.appendChild(card)
        }
        this.#updateColumnCounts()

        // Show a subtle error indicator on the card
        card.classList.add("ring-2", "ring-destructive/50")
        setTimeout(
          () => card.classList.remove("ring-2", "ring-destructive/50"),
          2000,
        )
      } else {
        // Update the card's status badge if present
        const badge = card.querySelector("[data-status-badge]")
        if (badge) {
          badge.textContent =
            column.dataset.statusLabel || toStatus.replace(/_/g, " ")
        }
        card.dataset.sourceStatus = toStatus
      }
    } catch (_error) {
      // Network error — revert
      const origColumn = this.element.querySelector(
        `[data-status="${sourceStatus}"]`,
      )
      const origCardList = origColumn?.querySelector("[data-kanban-cards]")
      if (origCardList) {
        origCardList.appendChild(card)
      }
      this.#updateColumnCounts()
    }
  }

  // ── Helpers ──────────────────────────────────────────────

  #updateColumnCounts() {
    this.columnTargets.forEach((col) => {
      const cards = col.querySelectorAll("[data-ticket-id]")
      const countEl = col.querySelector("[data-column-count]")
      if (countEl) {
        countEl.textContent = cards.length
      }
    })
  }
}
